'use client'

import { POST } from '@/api/post-requests';
import Cookies from 'js-cookie';
import { UserInterface } from '@/types/user';

interface GoogleLoginResponse {
  success: boolean;
  data?: {
    user: UserInterface;
    token: string;
  };
  errors?: Record<string, string[]>;
  message?: string;
}

interface GoogleLoginOptions {
  idToken: string;
  rememberMe?: boolean;
  callbackUrl?: string | null;
  onSuccess?: (user: UserInterface, token: string) => void;
  onError?: (error: string, errors?: Record<string, string[]>) => void;
}

/**
 * Login with Google by sending id_token to backend
 * 
 * This function:
 * 1. Sends id_token to /api/v1/auth/google
 * 2. Stores token in cookie (with optional 7-day expiry for remember-me)
 * 3. Returns user data and token
 * 4. Handles errors appropriately
 * 
 * @param {GoogleLoginOptions} options - Login options
 * @returns {Promise<{ success: boolean; user?: UserInterface; token?: string; needsPhoneSetup?: boolean }>}
 * 
 * @example
 * const result = await loginWithGoogle({
 *   idToken: 'google-id-token',
 *   rememberMe: true,
 *   callbackUrl: '/home',
 *   onSuccess: (user, token) => {
 *     setUser(user);
 *     router.replace('/home');
 *   },
 *   onError: (error) => {
 *     notifications.show(error, { severity: 'error' });
 *   }
 * });
 */
export const loginWithGoogle = async ({
  idToken,
  rememberMe = false,
  callbackUrl = null,
  onSuccess,
  onError,
}: GoogleLoginOptions): Promise<{
  success: boolean;
  user?: UserInterface;
  token?: string;
  needsPhoneSetup?: boolean;
  error?: string;
  errors?: Record<string, string[]>;
}> => {
  try {
    // Send id_token to backend
    const response: GoogleLoginResponse = await POST({
      url: 'v1/auth/google',
      body: {
        id_token: idToken,
      },
    });

    if (response.success && response.data) {
      const { user, token } = response.data;

      // Store token in cookie
      if (rememberMe) {
        Cookies.set('token', token, { expires: 7 });
      } else {
        Cookies.set('token', token);
      }

      // Check if user needs phone setup
      const needsPhoneSetup = !user.mobile_number || user.mobile_number.trim() === '';

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(user, token);
      }

      return {
        success: true,
        user,
        token,
        needsPhoneSetup,
      };
    } else {
      // Handle error response
      const errorMessage = response.message || 'Google login failed';
      const errors = response.errors || {};

      if (onError) {
        onError(errorMessage, errors);
      }

      return {
        success: false,
        error: errorMessage,
        errors,
      };
    }
  } catch (error) {
    // Handle network or other errors
    const errorMessage = error instanceof Error ? error.message : 'Network error occurred';

    if (onError) {
      onError(errorMessage);
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

